import { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import { ApiService } from "../../../config";
import { handleError } from "../../../utils/parseApiReturn";

export type ActivityEvent = "LOGIN" | "LOGIN_FALHOU" | "ACESSO" | "ALTERACAO" | "TELA" | "LOCALIZACAO";

export type ActivityLog = {
  id: number;
  createdAt: string;
  event: ActivityEvent;
  userId: number | null;
  userName: string | null;
  userEmail: string | null;
  userRole: string | null;
  description: string | null;
  method: string | null;
  path: string | null;
  status: number | null;
  profileRef: string | null;
  profileName: string | null;
  targetUserId: number | null;
  targetUserName: string | null;
  targetUserEmail: string | null;
  ip: string | null;
  userAgent: string | null;
  /** Cidade/UF/país pelo IP (base DB-IP). */
  geoCity: string | null;
  geoRegion: string | null;
  geoCountry: string | null;
  /** Posição do navegador (só eventos LOCALIZACAO). */
  lat: number | null;
  lon: number | null;
  accuracyM: number | null;
  /** Primeira vez que este usuário aparece com este IP. */
  ipNovo: boolean;
};

export type ActivityPage = {
  items: ActivityLog[];
  total: number;
  page: number;
  size: number;
};

export type VariosLugares = {
  userId: number;
  userName: string | null;
  userEmail: string | null;
  userRole: string | null;
  ips: number;
  aparelhos: number;
  listaIps: string | null;
  cidades: string | null;
  ultimo: string | null;
};

export type ActivitySummary = {
  horas: number;
  kpis: { logins: number; falhas: number; alteracoes: number; usuarios: number; ips: number };
  variosLugares: VariosLugares[];
};

export type ActivityQuery = {
  de?: string;
  ate?: string;
  userId?: number;
  event?: string;
  q?: string;
  ip?: string;
  /** 0-based, como o backend. */
  page: number;
  size: number;
};

const auth = (token: string, params?: object): AxiosRequestConfig => ({
  headers: { Authorization: `Bearer ${token}` },
  params,
});

const listar = async (query: ActivityQuery, token: string): Promise<ActivityPage | null> => {
  try {
    const result = await ApiService.get("/admin/activity-logs", auth(token, query));
    return result.data?.data ?? null;
  } catch (error) {
    toast.error(handleError(error, "Não foi possível carregar os logs.").message);
    return null;
  }
};

const resumo = async (horas: number, token: string): Promise<ActivitySummary | null> => {
  try {
    const result = await ApiService.get("/admin/activity-logs/summary", auth(token, { horas }));
    return result.data?.data ?? null;
  } catch (error) {
    toast.error(handleError(error, "Não foi possível carregar o resumo dos logs.").message);
    return null;
  }
};

export type PessoaOnline = {
  userId: number;
  userName: string | null;
  userEmail: string | null;
  userRole: string | null;
  online: boolean;
  route: string | null;
  screen: string | null;
  inicio: string | null;
  ultimo: string | null;
  ip: string | null;
  userAgent: string | null;
  geoCity: string | null;
  geoRegion: string | null;
  geoCountry: string | null;
  ipLat: number | null;
  ipLon: number | null;
  lat: number | null;
  lon: number | null;
  accuracyM: number | null;
  posicaoEm: string | null;
  geoStatus: string | null;
};

export type Online = { agora: string; online: number; pessoas: PessoaOnline[] };

type Num = number | string;

export type Painel = {
  dias: number;
  inicio: string;
  kpis: {
    usuarios: number;
    logins: number;
    negados: number;
    alteracoes: number;
    semPermissao: number;
    telas: number;
    comLocalizacao: number;
    ips: number;
    cidades: number;
    segundos: number;
  };
  porDia: { dia: string; usuarios: Num; logins: Num; negados: Num; alteracoes: Num; telas: Num; segundos: Num }[];
  porHora: { dow: number; hora: number; total: Num }[];
  usuarios: {
    userId: number;
    userName: string | null;
    userEmail: string | null;
    userRole: string | null;
    segundos: Num;
    alteracoes: Num;
    telas: Num;
    logins: Num;
    negados: Num;
    ips: Num;
    aparelhos: Num;
    cidades: string | null;
    diasAtivos: Num;
    ultimo: string | null;
  }[];
  perfis: { profileRef: string; profileName: string | null; alteracoes: Num; usuarios: Num; quem: string | null }[];
  telas: { tela: string; aberturas: Num; usuarios: Num }[];
  cidades: { cidade: string; uf: string | null; pais: string | null; usuarios: Num; eventos: Num }[];
  aparelhos: { userAgent: string; usuarios: Num }[];
};

const online = async (token: string): Promise<Online | null> => {
  try {
    const result = await ApiService.get("/admin/activity-logs/online", auth(token));
    return result.data?.data ?? null;
  } catch (error) {
    toast.error(handleError(error, "Não foi possível ver quem está online.").message);
    return null;
  }
};

const painel = async (dias: number, token: string): Promise<Painel | null> => {
  try {
    const result = await ApiService.get("/admin/activity-logs/dashboard", auth(token, { dias }));
    return result.data?.data ?? null;
  } catch (error) {
    toast.error(handleError(error, "Não foi possível carregar o painel de uso.").message);
    return null;
  }
};

export const ActivityLogsService = { listar, resumo, online, painel };
